import * as React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import breakpoints from '@westwing/ui-kit/breakPoints';
import { useRef, useMemo } from 'react';
import { Links } from '../types/Navigation';
import { HeaderModeType } from '../types/Header';
import { clubTheme } from './theme';
import type HeaderVariants from './HeaderVariants.json';
import ReferAFriend from './tray/ReferAFriend';
import CartIcon from './tray/CartIcon';
import Account from './tray/Account';
import ScopedStyleProvider from './ScopedStyleProvider';
import DynamicTitle from './DynamicTitle';
import NavigationBar from './NavigationBar';
import Share from './tray/Share';
import { SortButton } from './tray/SortButton';
import { HeaderStandaloneProps } from './types/headerStandalone';
import { useExternalProps } from './hooks/useExternalProps';
import WWEvents from './WWEvents';
import { ClubContextProvider } from './hooks/ClubContext';
import { prefixClubLinks } from './clubLinks';
import InfoBlockItem from './InfoBlockItem';
import { ClubLink } from './types/clubLinksTypes';
import useCurrentPageType from './hooks/useCurrentPageType';
import Header, { HeaderProps } from 'Components/Header';
import { HeaderContextProvider } from 'Components/HeaderContext';
import { Logo } from 'Components/Logo';
import { MockedStickyOverlayLine } from 'Components/Header/HeaderElements';
import ErrorBoundary from 'Components/ErrorBoundary';

const StyledMain = styled.div`
    font-family: ${({ theme }) => theme.fonts.fontBrandonRegular};
    background-color: ${({ theme }) => theme.colors.white};
`;

const createHeaderProps = ({
    variant = 'default',
    cart,
    t,
    title,
    loginState,
    linkPrefix,
    trackingProvider,
}: HeaderStandaloneProps): React.ComponentProps<typeof Header> => {
    const headerProps = {
        logoHref: `${linkPrefix}/`,
        tabsConfig: [
            {
                linkText: t('OwwHeader_Tab Shop'),
                linkUrl: linkPrefix + Links.BELLA_SSO,
                isSelected: false,
                trackingAction: 'Club-Shop tab click',
                noOverlay: true,
                testId: 'oww-header-shop-tab',
            },
            {
                linkText: t('OwwHeader_Tab Club'),
                linkUrl: `${linkPrefix}/`,
                isSelected: true,
                trackingAction: 'Club tab click',
                testId: 'oww-header-club-tab',
            },
        ] as HeaderProps['tabsConfig'],
        burgerMenuConfig: {
            onClick: () => {
                WWEvents.fire('BURGER_MENU_OPEN', undefined);
                trackingProvider.ga.trackNonInteractiveGenericEvent('Header', { action: 'Menu Show' });
            },
            dataTestId: 'oww-header-burger-menu',
        },
        iconTrayComponents: [
            <ReferAFriend key="refer-a-friend" displayOnMobile={variant !== 'cdp'} />,
            <ErrorBoundary fallbackComponent={null} key="account">
                <Account
                    key="account"
                    isLoggedIn={loginState?.isLoggedIn ?? false}
                    isPartiallyLoggedIn={loginState?.isPartiallyLoggedIn ?? false}
                />
            </ErrorBoundary>,
            <ErrorBoundary fallbackComponent={null} key="cart">
                <CartIcon
                    key="club-cart-icon"
                    formattedQuantity={cart.formattedQuantity}
                    secondsLeft={cart.secondsLeft}
                    remindAt={cart.remindAt}
                    showMinicartAt={cart.showMinicartAt}
                    products={cart.products}
                    totalPrice={cart.totalPrice}
                />
            </ErrorBoundary>,
        ],
        middleAreaComponent: <DynamicTitle>{title}</DynamicTitle>,
        infoBlockComponents: [
            <span key="free_return">{t('OwwHeader_Free Returns New')}</span>,
            <span key="days_to_return">{t('OwwHeader_Days for return header')}</span>,
            <InfoBlockItem
                key="help_center_link"
                pathName={ClubLink.HELPCENTER}
                text={t('OwwHeader_Customer Service')}
            />,
        ],
        headerMode: HeaderModeType.DEFAULT,
        miniHeaderConfig: {
            iconTrayComponents: [
                <CartIcon
                    key="club-cart-icon"
                    formattedQuantity={cart.formattedQuantity}
                    secondsLeft={cart.secondsLeft}
                    remindAt={cart.remindAt}
                    showMinicartAt={cart.showMinicartAt}
                    products={cart.products}
                    totalPrice={cart.totalPrice}
                />,
            ],
            handleBackButtonClick: () => window.history.back(),
            isLogoVisible: false,
            withShadow: variant !== 'cart',
            backButtonTestId: 'oww-header-mini-back',
        },
    };

    const variantHandlers: Record<HeaderVariant, () => void> = {
        cart: () => {
            headerProps.headerMode = HeaderModeType.MINI;
            headerProps.miniHeaderConfig.isLogoVisible = true;
        },
        cdp: () => {
            headerProps.iconTrayComponents = [<SortButton key="sortButton" />, ...headerProps.iconTrayComponents];
        },
        pdp: () => {
            headerProps.headerMode = HeaderModeType.MINI;
            headerProps.miniHeaderConfig.iconTrayComponents = [
                <ErrorBoundary key="share-button" fallbackComponent={null}>
                    <Share />
                </ErrorBoundary>,
                ...(headerProps.miniHeaderConfig.iconTrayComponents || []),
            ];
        },
        // eslint-disable-next-line camelcase
        top_only: () => {
            headerProps.iconTrayComponents = [
                ...(headerProps.iconTrayComponents || []),
                <div key="tray-top_only">Hello from top_only variant!</div>,
            ];
            headerProps.headerMode = HeaderModeType.FIRST_ROW;
        },
        default: () => undefined,
    };

    variantHandlers[variant]();
    return headerProps;
};

const getStickyHeaderStyles = (
    theme: DefaultTheme,
    isStickyEnabled = false,
    scope: string,
    stickyHeight?: string | null
    // eslint-disable-next-line max-params
) =>
    `
        #${scope} {
            z-index: ${theme.zindexOneHeaderNavBar};
        }

        @media all and (max-width: ${parseInt(breakpoints.breakpointTablet.replace('px', ''), 10) - 1}px) {
            #${scope} {
                position: ${isStickyEnabled ? 'sticky' : 'relative'};
                top: ${isStickyEnabled ? stickyHeight ?? `-${theme.owwHeader.mobileRowHeightSelected}px` : 'unset'};
            }
        }

        @media all and (min-width: ${breakpoints.breakpointTablet}) {
            #${scope} {
                top: ${isStickyEnabled ? stickyHeight ?? `-${theme.owwHeader.tabletRowHeightSelected}px` : 'unset'};
            }
        }

        @media all and (min-width: ${breakpoints.breakpointDesktop}) {
            #${scope} {
                position: relative;
                top: unset;
            }
        }
    `;

// TODO: remove Partial and pass all needed props
const HeaderStandalone = ({
    shouldFetchData = false,
    stickyAreaContainer,
    linkPrefix,
    serverSide,
    ...baseProps
}: HeaderStandaloneProps): JSX.Element => {
    const links = prefixClubLinks(linkPrefix);
    const props = useExternalProps({ ...baseProps, linkPrefix, serverSide }, links, shouldFetchData);
    const headerProps = useMemo(() => createHeaderProps(props), [props]);
    const {
        variant = 'default',
        selectedNavigationItem,
        t,
        scope,
        logger,
        trackingProvider,
        loginState,
        noOverlayForCOPNav,
    } = props;
    const stickyHeaderStyle = useRef(
        getStickyHeaderStyles(clubTheme, true, scope, headerProps.headerMode === HeaderModeType.MINI ? '0' : null)
    );
    const stickyRef = useRef<HTMLDivElement>(null);
    const [isLogoVisible, setIsLogoVisible] = React.useState<boolean>(false);
    const [showOnboardingTooltip, setShowOnboardingTooltip] = React.useState<boolean>(false);

    React.useEffect(() => {
        const showTooltip = () => setShowOnboardingTooltip(true);
        WWEvents.push({ event: 'SHOW_SHOP_ONBOARDING_TOOLTIP', listener: showTooltip });
        return () => WWEvents.remove('SHOW_SHOP_ONBOARDING_TOOLTIP', showTooltip);
    }, [setShowOnboardingTooltip]);

    React.useEffect(() => {
        if (!stickyAreaContainer || !stickyRef.current) {
            return;
        }
        stickyRef.current.appendChild(stickyAreaContainer);
    }, [stickyAreaContainer]);

    const stickyChangeHandler = (isVisible: boolean) => {
        setIsLogoVisible(isVisible);
    };

    return (
        <ScopedStyleProvider theme={clubTheme} scope={scope}>
            <HeaderContextProvider
                value={{
                    app: 'CLUB',
                    t,
                    logger,
                    trackingHelpers: { ...trackingProvider, useCurrentPageType },
                }}
            >
                <ClubContextProvider value={{ links, serverSide }}>
                    {/* eslint-disable-next-line react/no-danger */}
                    <style dangerouslySetInnerHTML={{ __html: stickyHeaderStyle.current }} />
                    <ErrorBoundary fallbackComponent={null}>
                        <StyledMain data-testid="oww-header">
                            <Header
                                tabsConfig={headerProps.tabsConfig}
                                burgerMenuConfig={headerProps.burgerMenuConfig}
                                iconTrayComponents={headerProps.iconTrayComponents}
                                middleAreaComponent={
                                    <>
                                        <MockedStickyOverlayLine
                                            $isHidden={!isLogoVisible}
                                            $numberOfTrayIcons={headerProps.iconTrayComponents?.length}
                                        >
                                            <Logo $appear $isHidden={!isLogoVisible} href={headerProps.logoHref} />
                                        </MockedStickyOverlayLine>
                                        {headerProps.middleAreaComponent}
                                    </>
                                }
                                afterHeaderComponent={
                                    <NavigationBar
                                        noOverlayForCOPNav={noOverlayForCOPNav}
                                        isLoggedIn={loginState.isLoggedIn && !loginState.isPartiallyLoggedIn}
                                        selectedNavigationItem={selectedNavigationItem}
                                    />
                                }
                                isStickyEnabled={variant !== 'top_only'}
                                infoBlockComponents={headerProps.infoBlockComponents}
                                headerMode={headerProps.headerMode}
                                miniHeaderConfig={headerProps.miniHeaderConfig}
                                stickyAreaComponents={[<div ref={stickyRef} key="sticky-area" />]}
                                logoHref={headerProps.logoHref}
                                onStickyChange={stickyChangeHandler}
                                isStuck={isLogoVisible}
                                showOnboardingTooltip={showOnboardingTooltip}
                            />
                        </StyledMain>
                    </ErrorBoundary>
                </ClubContextProvider>
            </HeaderContextProvider>
        </ScopedStyleProvider>
    );
};

// Exposes the header for Club to use
export default HeaderStandalone;

export type HeaderVariant = keyof typeof HeaderVariants;
